import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'
import type { UseFormSetError, FieldValues, Path } from 'react-hook-form'
import { isApiError } from '@/api/client'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/** Map 422 problem field errors into react-hook-form. Returns true when at least one field was mapped. */
export function applyFieldErrors<T extends FieldValues>(
  err: unknown,
  setError: UseFormSetError<T>,
  known: readonly string[],
  translate?: (field: string, msg: string) => string,
): boolean {
  if (!isApiError(err) || !err.isValidation) return false
  let mapped = false
  for (const [field, msg] of Object.entries(err.fields)) {
    if (known.includes(field)) {
      setError(field as Path<T>, { type: 'server', message: translate ? translate(field, msg) : msg })
      mapped = true
    }
  }
  return mapped
}

export function initials(name: string | undefined): string {
  if (!name) return '?'
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    return false
  }
}

/** Format a number with Ukrainian grouping. */
export function fmtNumber(n: number | null | undefined, digits = 0): string {
  if (n == null || Number.isNaN(n)) return '—'
  return new Intl.NumberFormat('uk-UA', { maximumFractionDigits: digits, minimumFractionDigits: digits }).format(n)
}

export function fmtMoney(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '—'
  return new Intl.NumberFormat('uk-UA', { style: 'currency', currency: 'UAH', maximumFractionDigits: 0 }).format(n)
}

export function fmtPercent(n: number | null | undefined, digits = 0): string {
  if (n == null || Number.isNaN(n)) return '—'
  return new Intl.NumberFormat('uk-UA', { style: 'percent', maximumFractionDigits: digits }).format(n)
}

/** Format seconds as a compact duration (1 год 5 хв / 45 с). */
export function fmtDuration(sec: number | null | undefined): string {
  if (sec == null) return '—'
  if (sec < 60) return `${Math.round(sec)} с`
  const m = Math.round(sec / 60)
  if (m < 60) return `${m} хв`
  const h = Math.floor(m / 60)
  const mm = m % 60
  if (h < 48) return mm ? `${h} год ${mm} хв` : `${h} год`
  return `${Math.round(h / 24)} д`
}

export function isEditableTarget(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false
  const tag = el.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable
}
